import { West, VideocamRounded } from '@mui/icons-material'
import {
  AzmoonTestiIcon,
  ClassroomIcon,
  ClearIcon,
  VideoClassIcon,
} from '@weblite/icons'
import { Box, ButtonBase, styled, SvgIcon, Typography } from '@mui/material'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import {
  FORM_CONFIG,
  HOMEWORK_CONFIG,
  POLL_CONFIG,
  UNIT_TEST_CONFIG,
} from '$common/wapp-bar-theme'
import {
  openFormCreator,
  openHomeWorkCreator,
  openMultipleChoiceExamCreator,
  openPollCreator,
  openPostCreator,
  openUnitTestCreator,
} from '$logic/wapps/business'
import { t } from '$i18n'
import { CommonOnboarding } from '$logic/onboarding/types'
import { WithOnboarding } from '$common/tour'
import { useStartCall } from '$common/room-app-bar/components/react-hooks'
import { useOpenedRoom } from '$logic/opened-room'
import { showChatListToolboxModal } from '$comp/modals/chat-list-toolbox'
import { OtherWapps } from '../other-wapps/other-wapps'

interface Props {
  onClose?: () => void
}

const CLOSE_BUTTON_WIDTH = 52

export const otherWappsConfig = [
  {
    ...POLL_CONFIG,
    onClick: openPollCreator,
    logoGradient: 'linear-gradient(180deg, #FFF35F 0%, #FFB100 100%)',
    key: 'POLL',
    onboarding: CommonOnboarding.POLL,
  },
  {
    ...HOMEWORK_CONFIG,
    onClick: openHomeWorkCreator,
    logoGradient: 'linear-gradient(180deg, #FF7600 0%, #E00000 100%)',
    key: 'HOMEWORK',
    onboarding: CommonOnboarding.HOME_WORK,
  },
  {
    ...FORM_CONFIG,
    onClick: openFormCreator,
    key: 'FORM',
    logoGradient: 'linear-gradient(180deg, #1DCBB0 0%, #008D77 100%)',
  },
  {
    ...UNIT_TEST_CONFIG,
    onClick: openUnitTestCreator,
    key: 'UNIT_TEST',
    logoGradient: 'linear-gradient(180deg, #00BAFF 0%, #093DEB 100%)',
    onboarding: CommonOnboarding.UNIT_TEST,
  },
]

export function ChatListToolboxHome({ onClose }: Props) {
  const { openedRoom } = useOpenedRoom()

  const roomType = openedRoom?.type
  const openedRoomId = openedRoom?.id
  const isChannel = roomType === 'CHANNEL'

  const { startingCall, openChooseCallDialog, canStartCall } = useStartCall({
    roomId: openedRoomId || '',
    hallType: 'CHANNEL',
  })

  return (
    <>
      <Header>
        <HeaderBackground />
        <CloseButton onClick={onClose}>
          <ClearIcon sx={{ width: '24px', height: '24px' }} />
        </CloseButton>
      </Header>
      <WappsGrid>
        <MainItemContainer
          sx={{
            borderColor: '#EF314F',
            gridColumn: '1 / span 2',
            py: '20px',
            px: '8px',
            display: 'flex',
            alignItems: 'flex-end',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          <SvgIcon
            viewBox="0 0 65 32"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            sx={{
              width: '65px',
              height: '32px',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <ellipse
              opacity="0.05"
              cx="18.5"
              cy="-17.5"
              rx="46.5"
              ry="49.5"
              fill="black"
            />
          </SvgIcon>

          <Box
            sx={{
              display: 'flex',
              gap: '8px',
              marginBottom: '12px',
              flexGrow: 1,
            }}
          >
            <AzmoonTestiIcon
              sx={{
                color: '#EF314F',
                width: '24px',
                height: '24px',
                flexShrink: 0,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                flexGrow: 1,
              }}
            >
              <Typography
                textAlign="right"
                variant="subtitle2"
                color="neutral900"
              >
                {t('chatListToolbox.mainItems.generalExam.title')}
              </Typography>
              <Typography
                textAlign="right"
                variant="paragraph3"
                color="neutral700"
              >
                {t('chatListToolbox.mainItems.generalExam.description')}
              </Typography>
            </Box>
          </Box>

          <WithOnboarding
            onboarding={CommonOnboarding.TEST_EXAM}
            hasFeatureOnboarding
            openCurrentModal={showChatListToolboxModal}
            sx={{ flexShrink: 0 }}
            onClick={async () => {
              await closeCurrentModal()
              openMultipleChoiceExamCreator()
            }}
          >
            <MainItemButton className="red">
              <Typography variant="caption1" color="inherit">
                {t('chatListToolbox.mainItems.generalExam.buttonText')}
              </Typography>
              <West sx={{ width: '16px', height: '16px' }} />
            </MainItemButton>
          </WithOnboarding>
        </MainItemContainer>
        {isChannel && !!openedRoomId && (
          <>
            <MainItemContainer
              sx={{
                borderColor: '#3559FC',
                height: '218px',
                py: '12px',
                px: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                justifyContent: 'space-between',
                opacity: canStartCall ? 1 : 0.6,
                pointerEvents: canStartCall ? 'auto' : 'none',
              }}
            >
              <SvgIcon
                viewBox="0 0 65 68"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                sx={{
                  width: '65px',
                  height: '68px',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <g opacity="0.05">
                  <line
                    x1="62.3186"
                    y1="-37.8787"
                    x2="-6.87837"
                    y2="31.3183"
                    stroke="black"
                    strokeWidth="6"
                  />
                  <line
                    x1="62.3186"
                    y1="-20.6814"
                    x2="-6.87837"
                    y2="48.5155"
                    stroke="black"
                    strokeWidth="6"
                  />
                  <line
                    x1="62.3186"
                    y1="-3.48415"
                    x2="-6.87837"
                    y2="65.7128"
                    stroke="black"
                    strokeWidth="6"
                  />
                </g>
              </SvgIcon>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <ClassroomIcon
                  sx={{ width: '24px', height: '24px', color: '#3559FC' }}
                />
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <Typography variant="subtitle2" color="neutral900">
                    {t('chatListToolbox.mainItems.classroom.title')}
                  </Typography>
                  <Typography variant="paragraph3" color="neutral700">
                    {t('chatListToolbox.mainItems.classroom.description')}
                  </Typography>
                </Box>
              </Box>

              <ClassroomButton
                canStartCall={canStartCall || false}
                startingCall={startingCall}
                openChooseCallDialog={openChooseCallDialog}
              />
            </MainItemContainer>
            <MainItemContainer
              sx={{
                borderColor: '#6436F7',
                height: '218px',
                py: '12px',
                px: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                justifyContent: 'space-between',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              >
                <VideocamRounded
                  sx={{
                    width: '70px',
                    height: '70px',
                    color: 'black',
                    opacity: '0.05',
                    transform: 'translateX(-7px) rotate(-30deg)',
                  }}
                />
              </Box>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
              >
                <VideoClassIcon
                  sx={{ width: '24px', height: '24px', color: '#6436F7' }}
                />
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                  <Typography variant="subtitle2" color="neutral900">
                    {t('chatListToolbox.mainItems.post.title')}
                  </Typography>
                  <Typography variant="paragraph3" color="neutral700">
                    {t('chatListToolbox.mainItems.post.description')}
                  </Typography>
                </Box>
              </Box>

              <WithOnboarding
                onboarding={CommonOnboarding.POST}
                openCurrentModal={showChatListToolboxModal}
                hasFeatureOnboarding
                sx={{ flexShrink: 0, width: '100%' }}
                onClick={async () => {
                  await closeCurrentModal()
                  openPostCreator(roomType)
                }}
              >
                <MainItemButton className="purple full-width">
                  <Typography variant="caption1" color="inherit">
                    {t('chatListToolbox.mainItems.post.buttonText')}
                  </Typography>
                  <West sx={{ width: '16px', height: '16px' }} />
                </MainItemButton>
              </WithOnboarding>
            </MainItemContainer>
          </>
        )}

        <Box
          sx={{
            gridColumn: '1 / span 2',
            display: 'grid',
            gridTemplateColumns: 'repeat(5, 1fr)',
          }}
        >
          <OtherWapps />
        </Box>
      </WappsGrid>
    </>
  )
}

function ClassroomButton({
  canStartCall,
  startingCall,
  openChooseCallDialog,
}: {
  canStartCall: boolean
  startingCall: boolean
  openChooseCallDialog: () => void
}) {
  return (
    <WithOnboarding
      hasFeatureOnboarding
      onboarding={CommonOnboarding.CLASSROOM}
      openCurrentModal={showChatListToolboxModal}
      sx={{
        flexShrink: 0,
        pointerEvents: canStartCall || startingCall ? 'auto' : 'none',
        width: '100%',
      }}
      onClick={async () => {
        await closeCurrentModal()
        openChooseCallDialog()
      }}
    >
      <MainItemButton
        className="blue full-width"
        disabled={!canStartCall || startingCall}
      >
        <Typography variant="caption1" color="inherit">
          {t('chatListToolbox.mainItems.classroom.buttonText')}
        </Typography>
        <West sx={{ width: '16px', height: '16px' }} />
      </MainItemButton>
    </WithOnboarding>
  )
}

const Header = styled('div')`
  margin-top: ${CLOSE_BUTTON_WIDTH / 2}px;
  width: 100%;
  height: 40px;
`

const HeaderBackground = styled('div')`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 16px 16px 0 0;
  position: relative;
  &:after {
    content: '';
    position: absolute;
    width: 62px;
    height: 62px;
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    box-shadow: 0 0 0 2000px ${({ theme }) => theme.palette.neutral000};
  }
`

const CloseButton = styled(ButtonBase)`
  width: ${CLOSE_BUTTON_WIDTH}px;
  height: ${CLOSE_BUTTON_WIDTH}px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  &:hover {
    background-color: ${({ theme }) => theme.palette.neutral100};
  }
  color: ${({ theme }) => theme.palette.neutral700};
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  border-radius: 50%;
`

const WappsGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  background-color: ${({ theme }) => theme.palette.neutral000};
  padding: 8px 12px 12px;
`

const MainItemContainer = styled('div')`
  border-radius: 12px 12px 12px 0;
  border-width: 4px;
  border-style: solid;
  position: relative;
`

const MainItemButton = styled(ButtonBase)`
  &.full-width {
    width: 100%;
  }
  &.red {
    background: linear-gradient(180deg, #ef4460 0%, #ef314f 100%);
  }
  &.blue {
    background: linear-gradient(180deg, #2688ff 0%, #3559fc 100%);
  }
  &.purple {
    background: linear-gradient(180deg, #885af8 0%, #6436f7 100%);
  }

  color: ${({ theme }) => theme.palette.neutral000};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 36px;
  padding: 0 12px;
  border-radius: 12px;
`
